import { noConvert } from 'components/inline_edit/InlineEditor';
import { useEffectSkipFirst } from 'custom_hooks/useEffectSkipFirst';
import { useIsMounted } from 'custom_hooks/useIsMounted';
import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

export type PendingValue<TValue> = Readonly<{ pending: TValue }>;

export type InlineEditorState = 'displaying' | 'editing' | 'confirming';

export type ErrorMessage = string;

export type OnConfirmCallback<TValue> = (
    value: TValue
) => Promise<ErrorMessage | void> | ErrorMessage | void;

export type ValidateCallback<TInput> = (
    inputValue: TInput | undefined
) => Promise<ErrorMessage | undefined> | ErrorMessage | undefined;

export type SerializedInlineEditorResult<TValue, TInput> = Readonly<{
    valueToRender: TValue | undefined;
    inputValue: TInput | undefined;
    setInputValue: Dispatch<SetStateAction<TInput | undefined>>;
    error: string | undefined;
    isEditing: boolean;
    isConfirming: boolean;
    openEditor: () => void;
    confirm: () => Promise<void>;
    closeEditor: () => void;
    canConfirm: boolean;
    state: InlineEditorState;
}>;

export type UseSerializedInlineEditorOptions<TValue, TInput> = Readonly<{
    /**
     * The current value.
     */
    value: TValue | undefined;
    /**
     * Callback to validate input.
     */
    validate?: ValidateCallback<TValue>;
    /**
     * Callback for confirming (storing value, saving, updating, etc...).
     */
    onConfirm: OnConfirmCallback<TValue | undefined>;
    /**
     * Callback for when the editor is closed.
     */
    onClose?: () => void;
    /**
     * Converts the current value to the needed type for the input element.
     */
    serialize: (value: TValue | undefined) => TInput | undefined;
    /**
     * Converts an input value to the type to the type of the current value.
     */
    deserialize: (inputValue: TInput | undefined) => TValue | undefined;
}>;

export function useSerializedInlineEditor<TValue, TInput>({
    value,
    validate,
    serialize,
    deserialize,
    onConfirm,
    onClose,
}: UseSerializedInlineEditorOptions<TValue, TInput>): SerializedInlineEditorResult<
    TValue,
    TInput
> {
    const serializeRef = useRef(serialize);
    serializeRef.current = serialize;
    const deserializeRef = useRef(deserialize);
    deserializeRef.current = deserialize;
    const validateRef = useRef(validate);
    validateRef.current = validate;
    const onConfirmRef = useRef(onConfirm);
    onConfirmRef.current = onConfirm;
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const isMounted = useIsMounted();
    const [state, setState] = useState<InlineEditorState>('displaying');
    const [inputValue, setInputValue] = useState<TInput | undefined>(
        serializeRef.current(value)
    );
    const [pendingValue, setPendingValue] = useState<PendingValue<TValue | undefined>>();
    const [validationError, setValidationError] = useState<ErrorMessage | undefined>();
    const [confirmationError, setConfirmationError] = useState<
        ErrorMessage | undefined
    >();

    const setInputValueFn = useCallback<Dispatch<SetStateAction<TInput | undefined>>>(
        (newValue) => {
            if (state === 'confirming') {
                throw new Error('Cannot update input value while confirming');
            }
            setInputValue(newValue);
        },
        [state]
    );

    const resetInputValue = useCallback(() => {
        setInputValue(serializeRef.current(value));
        Promise.resolve(validateRef.current?.(value)).then(setValidationError);
    }, [value]);

    const openEditor = useCallback(() => {
        if (state === 'displaying') {
            resetInputValue();
            setState('editing');
        }
    }, [state, resetInputValue]);

    const closeEditor = useCallback(() => {
        if (state === 'confirming') {
            throw new Error('Cannot cancel while confirming');
        }
        if (state === 'editing') {
            resetInputValue();
            setState('displaying');
            onCloseRef.current?.();
        }
    }, [state, resetInputValue]);

    const confirm = useCallback(async () => {
        if (state === 'confirming') {
            throw new Error('Already confirming');
        }
        setConfirmationError(undefined);
        const convertedInputValue = deserializeRef.current(inputValue);
        const error = await Promise.resolve(validateRef.current?.(convertedInputValue));
        if (error) {
            setValidationError(error);
        } else {
            setPendingValue({ pending: convertedInputValue });
            setValidationError(undefined);
            setState('confirming');

            try {
                const result = await Promise.resolve(
                    onConfirmRef.current(convertedInputValue)
                );
                if (isMounted.current) {
                    if (typeof result === 'string') {
                        setState('editing');
                        setPendingValue(undefined);
                        setConfirmationError(result);
                    } else {
                        setState('displaying');
                        onCloseRef.current?.();
                    }
                }
            } catch (e) {
                setState('editing');
                setPendingValue(undefined);
                setConfirmationError(
                    e instanceof Error ? e.message : 'Unable to confirm'
                );
            }
        }
    }, [inputValue, isMounted, state]);

    useEffect(() => setPendingValue(undefined), [value]);

    useEffectSkipFirst(() => {
        setConfirmationError(undefined);
        Promise.resolve(validateRef.current?.(deserializeRef.current(inputValue))).then(
            setValidationError
        );
    }, [inputValue]);

    return {
        valueToRender: pendingValue ? pendingValue.pending : value,
        inputValue,
        setInputValue: setInputValueFn,
        error: validationError ?? confirmationError,
        isEditing: state === 'editing',
        isConfirming: state === 'confirming',
        openEditor,
        confirm,
        closeEditor,
        canConfirm: state === 'editing' && validationError === undefined,
        state,
    };
}

export type InlineEditorResult<TValue> = SerializedInlineEditorResult<TValue, TValue>;

export type UseInlineEditorOptions<TValue> = Omit<
    UseSerializedInlineEditorOptions<TValue, TValue>,
    'serialize' | 'deserialize'
>;

export function useInlineEditor<TValue>(
    props: UseInlineEditorOptions<TValue>
): InlineEditorResult<TValue> {
    return useSerializedInlineEditor<TValue, TValue>({
        ...props,
        serialize: noConvert,
        deserialize: noConvert,
    });
}
